import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import TestAPI from './api';
import { TestCase } from './entity';
import { fetchTestsuites } from './testsuites.slice';
import { create } from 'domain';

export const TEST_SPECIFICATIONS_FEATURE_KEY = 'testSpecifications';

/*
 * Update these interfaces according to your requirements.
 */
export type TestSpecificationsEntity = TestCase;

export interface TestSpecificationsState
  extends EntityState<TestSpecificationsEntity, number> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error';
  savingStatus: 'idle' | 'saving' | 'saved' | 'error';
  error?: string | null;
}

export const testSpecificationsAdapter =
  createEntityAdapter<TestSpecificationsEntity>();

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchTestSpecifications())
 * }, [dispatch]);
 * ```
 */
export const fetchTestSpecifications = createAsyncThunk<
  TestSpecificationsEntity[],
  number
>('testSpecifications/fetchStatus', async (testSuiteId: number, thunkAPI) => {
  /**
   * Replace this with your custom fetch call.
   * For example, `return myApi.getTestSpecificationss()`;
   * Right now we just return an empty array.
   */
  const response = await TestAPI.fetchTestSpecifications(testSuiteId);
  console.info('fetchTestSpecifications response', response.body?.data);

  thunkAPI.dispatch(fetchTestsuites());

  return response.body?.data as TestSpecificationsEntity[];
});

export const saveTestSpecification = createAsyncThunk<
  TestSpecificationsEntity,
  TestSpecificationsEntity
>(
  'testSpecifications/save',
  async (testSpecification: TestSpecificationsEntity, thunkAPI) => {
    let response;
    if (testSpecification.id) {
      response = await TestAPI.updateTestSpecification(testSpecification);
    } else {
      response = await TestAPI.saveTestSpecification(testSpecification);
    }

    console.info('saveTestSpecification response', response.body);
    if (response.ok) {
      return response.body as TestSpecificationsEntity;
    }

    return thunkAPI.rejectWithValue(response.body);
  }
);

export const deleteTestSpecification = createAsyncThunk<
  TestSpecificationsEntity,
  TestSpecificationsEntity
>(
  'testSpecifications/delete',
  async (testSpecification: TestSpecificationsEntity, thunkAPI) => {
    let response;
    if (testSpecification.id) {
      response = await TestAPI.deleteTestSpecification(testSpecification);
    } else {
      response = await TestAPI.saveTestSpecification(testSpecification);
    }

    console.info('deleteTestSpecification response', response.body);
    if (response.ok) {
      return response.body as TestSpecificationsEntity;
    }

    return thunkAPI.rejectWithValue(response.body);
  }
);

export const initialTestSpecificationsState: TestSpecificationsState =
  testSpecificationsAdapter.getInitialState({
    loadingStatus: 'not loaded',
    savingStatus: 'idle',
    error: null,
  });

export const testSpecificationsSlice = createSlice({
  name: TEST_SPECIFICATIONS_FEATURE_KEY,
  initialState: initialTestSpecificationsState,
  reducers: {
    add: testSpecificationsAdapter.addOne,
    remove: testSpecificationsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchTestSpecifications.pending,
        (state: TestSpecificationsState) => {
          state.loadingStatus = 'loading';
        }
      )
      .addCase(
        fetchTestSpecifications.fulfilled,
        (
          state: TestSpecificationsState,
          action: PayloadAction<TestSpecificationsEntity[]>
        ) => {
          testSpecificationsAdapter.setAll(state, action.payload);
          state.loadingStatus = 'loaded';
        }
      )
      .addCase(
        fetchTestSpecifications.rejected,
        (state: TestSpecificationsState, action) => {
          state.loadingStatus = 'error';
          state.error = action.error.message;
        }
      )
      .addCase(saveTestSpecification.pending, (state) => {
        state.savingStatus = 'saving';
      })
      .addCase(saveTestSpecification.fulfilled, (state, action) => {
        console.info('saveTestSpecification.fulfilled', action.payload);
        testSpecificationsAdapter.upsertOne(state, action.payload);
        state.savingStatus = 'saved';
      })
      .addCase(saveTestSpecification.rejected, (state, action) => {
        state.savingStatus = 'error';
        state.error = action.error.message;
      })
      .addCase(deleteTestSpecification.pending, (state) => {
        state.savingStatus = 'saving';
      })
      .addCase(deleteTestSpecification.fulfilled, (state, action) => {
        console.info('deleteTestSpecification.fulfilled', action.meta.arg);
        testSpecificationsAdapter.removeOne(state, action.meta.arg.id);
        state.savingStatus = 'saved';
      })
      ;
  },
});

/*
 * Export reducer for store configuration.
 */
export const testSpecificationsReducer = testSpecificationsSlice.reducer;

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(testSpecificationsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const testSpecificationsActions = testSpecificationsSlice.actions;

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
import { test } from '@playwright/test';
import { fetchTestSuites } from './api/fetchTestSuites';
 *
 * // ...
 *
 * const entities = useSelector(selectAllTestSpecifications);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities } = testSpecificationsAdapter.getSelectors();

export const getTestSpecificationsState = (rootState: {
  [TEST_SPECIFICATIONS_FEATURE_KEY]: TestSpecificationsState;
}): TestSpecificationsState => rootState[TEST_SPECIFICATIONS_FEATURE_KEY];

export const selectAllTestSpecifications = createSelector(
  getTestSpecificationsState,
  selectAll
);

export const selectTestSpecificationsEntities = createSelector(
  getTestSpecificationsState,
  selectEntities
);

export const selectTestSpecificationLoadingStatus = createSelector(
  getTestSpecificationsState,
  (state) => state.loadingStatus
);

export const selectTestSpecificationSavingStatus = createSelector(
  getTestSpecificationsState,
  (state) => state.savingStatus
);

// export const selectTestSpecificationsByTestSuiteId = (testSuiteId: number) => createSelector(
//   getTestSpecificationsState,
//   (state) => {
//     return Object.values(state.entities).filter((testSpecification) => testSpecification.test_suite_id === testSuiteId);
//   }
// );

// export const selectTestSpecificationsByTestSuiteId = (testSuiteId: number) =>
//   createSelector(
//     [selectAllTestSpecifications], // Use the selectAllTestSpecifications selector to get all test specifications
//     (testSpecifications) =>
//       testSpecifications.filter((testSpecification) =>
//         testSpecification.test_suite_id === testSuiteId
//       )
//   );

export const selectTestSpecificationsByTestSuiteId = createSelector(
  [selectAllTestSpecifications, (_, testSuiteId: number) => testSuiteId],
  (testSpecifications, testSuiteId) =>
    testSpecifications.filter(
      (testSpecification) => testSpecification.test_suite_id === testSuiteId
    )
);
